import { memo } from 'react';
import type { CounterLogDTO, DayLogsDTO } from '@six-pockets/shared';
import { PencilIcon } from '@/components/icons';
import { Button } from '@/components/ui';
import { formatDayLabel, toDateKey } from '@/utils/date';
import { formatClock, formatDateTime, formatINR } from '@/utils/format';
import { describeNotes } from '../cashNotes';
import styles from '@/styles/components/LedgerRow/style.scss';

/** One day's logs: day, when logged (and when edited) · cash count · UPI count · edit. "—" for one not logged. */
export const CounterLogRow = memo(function CounterLogRow({ logs, onEdit }: { logs: DayLogsDTO; onEdit: (l: DayLogsDTO) => void }) {
  const made = [logs.cash, logs.upi].filter((l): l is CounterLogDTO => l !== null);
  // A cash count added on a later day sits at the last moment of its own day (the closing figure), which is not when
  // staff entered it: for that one, when it was last saved is.
  const addedLater = (l: CounterLogDTO) => l.kind === 'cash' && new Date(Date.parse(l.createdAt) + 1).getHours() === 0 && toDateKey(new Date(l.updatedAt)) > l.day;
  const loggedAt = made.map((l) => (addedLater(l) ? l.updatedAt : l.createdAt)).sort()[0];
  // Saved again more than a minute after logging: a correction.
  const editedAt = made
    .filter((l) => !addedLater(l) && Date.parse(l.updatedAt) - Date.parse(l.createdAt) > 60_000)
    .map((l) => l.updatedAt)
    .sort()
    .at(-1);
  const sameDay = loggedAt && toDateKey(new Date(loggedAt)) === logs.day;

  return (
    <li className={styles.row}>
      <div className={styles.main}>
        <span className={styles.title}>{formatDayLabel(logs.day)}</span>
        <span className={styles.meta}>
          {loggedAt && `logged ${sameDay ? formatClock(loggedAt) : formatDateTime(loggedAt)}`}
          {editedAt && ` · edited ${formatDateTime(editedAt)}`}
        </span>
      </div>
      <span className={styles.figure} title={logs.cash?.notes ? describeNotes(logs.cash.notes) : undefined}>
        <span className={styles.figureLabel}>Cash</span>
        {logs.cash ? formatINR(logs.cash.amount) : '—'}
      </span>
      <span className={styles.figure}>
        <span className={styles.figureLabel}>UPI</span>
        {logs.upi ? formatINR(logs.upi.amount) : '—'}
      </span>
      <div className={styles.actions}>
        <Button
          size="sm"
          variant="ghost"
          iconOnly
          icon={<PencilIcon size={15} />}
          aria-label={`Edit the count for ${formatDayLabel(logs.day)}`}
          title="Edit"
          onClick={() => onEdit(logs)}
        />
      </div>
    </li>
  );
});
